import { elementName } from './element-names';
import { LEWIS_SIDES, parseLewisValue } from './reaction-lewis';
import { tokenize, type Token } from './reaction-tokens';

import type { MarkKind } from './reaction-marks';

import i18n from '@/lib/i18n';

/**
 * A reaction as the sentence a screen reader is given.
 *
 * ── WHY THIS IS THE REAL THING, NOT A PREVIEW ─────────────────────────────────────────────────
 * The string built here is written onto the rendered node as its accessible name, so a screen
 * reader is handed THIS and never sees the markup underneath. Preview's read-through then reads the
 * same string, which is what makes it a preview at all rather than a second implementation that
 * happens to sit alongside the first.
 *
 * That is worth doing for chemistry specifically. KaTeX emits presentation MathML — layout, not
 * meaning — so a reader applies MATHS rules to it and H₂O arrives as "H sub 2 O", arrows as
 * "rightwards arrow" or nothing. Equations are left alone precisely because the opposite is true
 * there: a maths engine reads KaTeX's MathML better than any string we would write.
 *
 * ── LETTERS, NOT NAMES ────────────────────────────────────────────────────────────────────────
 * `Fe` is "F E", not "iron". A screen reader's job is to convey the notation, and translating a
 * symbol is interpretation we can get wrong — a bare `Fe` might be the metal, an ion, or a
 * variable. A student learning notation needs the notation; one who knows it can reconstruct the
 * name, and not the reverse.
 *
 * Nuclides are the exception, and a principled one: `^{238}_{92}U` is "uranium 238". A mass number
 * makes the context unambiguous, which was the whole objection to names elsewhere. The atomic
 * number is dropped as redundant with the symbol, which is the established convention.
 *
 * ── AND WHY THE LETTERS ARE SPACED ────────────────────────────────────────────────────────────
 * Any letter run that spells a word gets read as one: `OH-` becomes "Ohio", `CO2` becomes "co two",
 * `NO3-` becomes "no three". Spacing them prevents the voice forming a word, and spacing digits
 * away from symbols stops it guessing at token boundaries.
 *
 * What none of this fixes, because speech cannot: a coefficient sounds like a subscript, so `2H2O`
 * is "2 H 2 O" either way. That is why the row carries a note rather than pretending otherwise.
 */

/**
 * Symbol to name, for nuclides only.
 *
 * One locale key holding the whole table rather than 118 keys per language. It is a single
 * translatable resource that never changes, and as separate keys it would be a fifth of the locale
 * file. Parsed once, on first use.
 */
/** Particles, longest pattern first so `\beta^-` is not matched as `\beta`. */
const PARTICLES: [RegExp, SpeechKey][] = [
  [/\\bar\{\\nu\}(\{\})?/g, 'antineutrino'],
  [/\\beta\^-(\{\})?/g, 'betaMinus'],
  [/\\beta\^\+(\{\})?/g, 'betaPlus'],
  [/\\alpha(\{\})?/g, 'alpha'],
  [/\\gamma(\{\})?/g, 'gamma'],
  // NOT the nu inside `h\nu`, which is a photon and is handled with the conditions.
  [/(?<!h)\\nu(\{\})?/g, 'neutrino'],
  [/e\^-(\{\})?/g, 'electron'],
  [/e\^\+(\{\})?/g, 'positron'],
];

/**
 * Arrows and what each one is called, longest first: `<-` is inside `<->`, `<=>` inside `<=>>`.
 *
 * BOTH SPELLINGS of every one — the mhchem the teacher stored, and the single character the
 * display table turns it into. A reaction carrying any MARK is tokenised to display text before
 * this pass runs, so `[#A324BA:A] -> B` reached it as `→` and was read as the bare character.
 * Every arrow in a coloured or struck reaction was affected, which is why both are listed.
 */
const ARROWS: [string, SpeechKey][] = [
  ['<=>>', 'equilibriumProducts'],
  ['<<=>', 'equilibriumReactants'],
  ['<=>', 'equilibrium'],
  ['\u21CC', 'equilibrium'],
  ['<-->', 'bothWaysArrow'],
  ['\u21C4', 'bothWaysArrow'],
  ['<->', 'bothWays'],
  ['\u2194', 'bothWays'],
  ['->', 'yields'],
  ['\u2192', 'yields'],
  ['\u27F6', 'yields'],
  ['<-', 'producedFrom'],
  ['\u2190', 'producedFrom'],
  /* The two LaTeX arrows. Without these the escape reached the reader as letters: a
     retrosynthetic arrow was read "backslash R I G H T A R R O W". */
  ['\\Rightarrow', 'retrosynthetic'],
  ['\u21D2', 'retrosynthetic'],
  ['\\dashrightarrow', 'dashedArrow'],
  ['\u21E2', 'dashedArrow'],
];

const escapeCode = (code: string) => code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * What a LABEL says, which is not always what it is written as.
 *
 * `\Delta` and `$h\nu$` are the two the palette offers and both are LaTeX: read as written they
 * come out as backslashes and dollar signs. The dollars matter — the photon key inserts
 * `$h\nu$`, maths mode so both letters are italic, and the old reading tested `h\nu` without
 * them and so never matched the thing the button actually wrote.
 */
const bareLabel = (raw: string) => raw.trim().replace(/^\$+|\$+$/g, '').trim();
/* Both spellings again: a reaction carrying a mark reaches this pass as DISPLAY text, so the
   label of a struck or coloured heated arrow arrives as `Δ` rather than as `\Delta` and was
   read out as the bare Greek letter — "yields with Δ". */
const isHeat = (raw: string) => ['\\Delta', '\u0394'].includes(bareLabel(raw));
const isLight = (raw: string) => ['h\\nu', 'h\u03BD'].includes(bareLabel(raw));

const STATES: Record<string, SpeechKey> = { s: 'solid', l: 'liquid', g: 'gas', aq: 'aqueous' };

type SpeechKey =
  | 'nuclide'
  | 'alpha'
  | 'betaMinus'
  | 'betaPlus'
  | 'gamma'
  | 'neutrino'
  | 'antineutrino'
  | 'electron'
  | 'positron'
  | 'yields'
  | 'producedFrom'
  | 'bothWays'
  | 'bothWaysArrow'
  | 'phaseBoundary'
  | 'saltBridge'
  | 'doubleBond'
  | 'tripleBond'
  | 'highlighted'
  | 'endHighlighted'
  | 'crossedOut'
  | 'endCrossedOut'
  | 'oxidationState'
  | 'italic'
  | 'lonePairAbove'
  | 'unpairedAbove'
  | 'lonePairRight'
  | 'unpairedRight'
  | 'lonePairBelow'
  | 'unpairedBelow'
  | 'lonePairLeft'
  | 'unpairedLeft'
  | 'equilibrium'
  | 'equilibriumProducts'
  | 'equilibriumReactants'
  | 'yieldsHeat'
  | 'yieldsLight'
  | 'yieldsWith'
  | 'retrosynthetic'
  | 'coSubstrateIn'
  | 'coSubstrateOut'
  | 'dashedArrow'
  | 'antiparticle'
  | 'arrowWithAbove'
  | 'arrowWithBelow'
  | 'arrowWithBoth'
  | 'solid'
  | 'liquid'
  | 'gas'
  | 'aqueous'
  | 'dot'
  | 'degrees'
  | 'heat'
  | 'photon'
  | 'chargePlus'
  | 'chargeMinus'
  | 'chargePlusN'
  | 'chargeMinusN'
  | 'gasEvolved'
  | 'precipitates'
  | 'plus'
  | 'openBracket'
  | 'closeBracket';

/**
 * Every phrase as a LITERAL `t()` call.
 *
 * A table-driven `t(`reactions.speech.${key}`)` is the obvious way to write this and trips the
 * dynamic-key ceiling in `invariants.test.ts` — it did, on the first run, for the third time in
 * this area. A `Record<SpeechKey, string>` lookup would be worse still: invisible to that ceiling
 * AND to the every-key-exists check, so a missing translation would ship silently. The exhaustive
 * switch means a new phrase fails to compile until it has a key.
 */
function say(key: SpeechKey, opts?: Record<string, unknown>): string {
  switch (key) {
    case 'nuclide': return i18n.t('reactions.speech.nuclide', opts);
    case 'alpha': return i18n.t('reactions.speech.alpha', opts);
    case 'betaMinus': return i18n.t('reactions.speech.betaMinus', opts);
    case 'betaPlus': return i18n.t('reactions.speech.betaPlus', opts);
    case 'gamma': return i18n.t('reactions.speech.gamma', opts);
    case 'neutrino': return i18n.t('reactions.speech.neutrino', opts);
    case 'antineutrino': return i18n.t('reactions.speech.antineutrino', opts);
    case 'electron': return i18n.t('reactions.speech.electron', opts);
    case 'positron': return i18n.t('reactions.speech.positron', opts);
    case 'yields': return i18n.t('reactions.speech.yields', opts);
    case 'producedFrom': return i18n.t('reactions.speech.producedFrom', opts);
    case 'bothWays': return i18n.t('reactions.speech.bothWays', opts);
    case 'equilibrium': return i18n.t('reactions.speech.equilibrium', opts);
    case 'equilibriumProducts': return i18n.t('reactions.speech.equilibriumProducts', opts);
    case 'equilibriumReactants': return i18n.t('reactions.speech.equilibriumReactants', opts);
    case 'yieldsHeat': return i18n.t('reactions.speech.yieldsHeat', opts);
    case 'yieldsLight': return i18n.t('reactions.speech.yieldsLight', opts);
    case 'yieldsWith': return i18n.t('reactions.speech.yieldsWith', opts);
    case 'retrosynthetic': return i18n.t('reactions.speech.retrosynthetic', opts);
    case 'coSubstrateIn': return i18n.t('reactions.speech.coSubstrateIn', opts);
    case 'coSubstrateOut': return i18n.t('reactions.speech.coSubstrateOut', opts);
    case 'dashedArrow': return i18n.t('reactions.speech.dashedArrow', opts);
    case 'antiparticle': return i18n.t('reactions.speech.antiparticle', opts);
    case 'arrowWithAbove': return i18n.t('reactions.speech.arrowWithAbove', opts);
    case 'arrowWithBelow': return i18n.t('reactions.speech.arrowWithBelow', opts);
    case 'arrowWithBoth': return i18n.t('reactions.speech.arrowWithBoth', opts);
    case 'solid': return i18n.t('reactions.speech.solid', opts);
    case 'liquid': return i18n.t('reactions.speech.liquid', opts);
    case 'gas': return i18n.t('reactions.speech.gas', opts);
    case 'aqueous': return i18n.t('reactions.speech.aqueous', opts);
    case 'dot': return i18n.t('reactions.speech.dot', opts);
    case 'degrees': return i18n.t('reactions.speech.degrees', opts);
    case 'heat': return i18n.t('reactions.speech.heat', opts);
    case 'photon': return i18n.t('reactions.speech.photon', opts);
    case 'chargePlus': return i18n.t('reactions.speech.chargePlus', opts);
    case 'chargeMinus': return i18n.t('reactions.speech.chargeMinus', opts);
    case 'chargePlusN': return i18n.t('reactions.speech.chargePlusN', opts);
    case 'chargeMinusN': return i18n.t('reactions.speech.chargeMinusN', opts);
    case 'gasEvolved': return i18n.t('reactions.speech.gasEvolved', opts);
    case 'precipitates': return i18n.t('reactions.speech.precipitates', opts);
    case 'plus': return i18n.t('reactions.speech.plus', opts);
    case 'openBracket': return i18n.t('reactions.speech.openBracket', opts);
    case 'closeBracket': return i18n.t('reactions.speech.closeBracket', opts);
    case 'bothWaysArrow': return i18n.t('reactions.speech.bothWaysArrow', opts);
    case 'phaseBoundary': return i18n.t('reactions.speech.phaseBoundary', opts);
    case 'saltBridge': return i18n.t('reactions.speech.saltBridge', opts);
    case 'doubleBond': return i18n.t('reactions.speech.doubleBond', opts);
    case 'tripleBond': return i18n.t('reactions.speech.tripleBond', opts);
    case 'highlighted': return i18n.t('reactions.speech.highlighted', opts);
    case 'endHighlighted': return i18n.t('reactions.speech.endHighlighted', opts);
    case 'crossedOut': return i18n.t('reactions.speech.crossedOut', opts);
    case 'endCrossedOut': return i18n.t('reactions.speech.endCrossedOut', opts);
    case 'oxidationState': return i18n.t('reactions.speech.oxidationState', opts);
    case 'italic': return i18n.t('reactions.speech.italic', opts);
    case 'lonePairAbove': return i18n.t('reactions.speech.lonePairAbove', opts);
    case 'unpairedAbove': return i18n.t('reactions.speech.unpairedAbove', opts);
    case 'lonePairRight': return i18n.t('reactions.speech.lonePairRight', opts);
    case 'unpairedRight': return i18n.t('reactions.speech.unpairedRight', opts);
    case 'lonePairBelow': return i18n.t('reactions.speech.lonePairBelow', opts);
    case 'unpairedBelow': return i18n.t('reactions.speech.unpairedBelow', opts);
    case 'lonePairLeft': return i18n.t('reactions.speech.lonePairLeft', opts);
    case 'unpairedLeft': return i18n.t('reactions.speech.unpairedLeft', opts);
  }
}

/**
 * Element symbols and digits, spaced so neither the voice nor the listener has to guess.
 *
 * Runs only on what is left after every phrase has been lifted out, so it can never chew through a
 * word this module inserted.
 */
function spellSpecies(text: string): string {
  return text
    .replace(/[A-Za-z]+/g, run => run.split('').join(' ').toUpperCase())
    .replace(/(\d+)/g, ' $1 ');
}

/**
 * SAYING WHAT IS MARKED, AND NOTHING ABOUT WHY.
 *
 * A mark is announced the way a screen reader announces formatting: the word before the thing.
 * "Highlighted C O O H" — the marker and what it covers, and there it stops. Naming the REASON
 * would hand a blind student an analysis the sighted student had to make for themselves. A
 * highlight calls attention to a component; it does not say what is interesting about it, and in
 * a question that asks what changed, saying so would give the answer away.
 *
 * A span closes only when it covers more than one token. "Highlighted 2 H 2 O" needs no end;
 * "highlighted 2 H 2 O, plus, O 2, yields" does, or nothing says where the highlight stopped.
 * A span across tokens is stored as one mark per token, so a run of neighbours carrying the same
 * colour IS the multi-token case.
 */
function markedUp(formula: string, hold: (t: string, tight?: true) => string): string {
  const toks = tokenize(formula);
  if (!toks.some(t => t.marks?.length || t.structure)) return formula;

  const covering = (t: Token, kind: MarkKind) =>
    (t.marks ?? []).find(m => m.kind === kind && m.start === 0 && m.end === t.text.length);

  /** One token's text with its own marks spoken, skipping the kinds a span already announced. */
  const inner = (t: Token, skip: MarkKind[]): string => {
    /* A drawn molecule says its name. Its SMILES read aloud is "C 1 C C C C C 1", which is
       worse than silence — and the name is carried on the token precisely because nothing can
       derive one. */
    if (t.structure) return hold(t.structure.name);
    const marks = (t.marks ?? []).filter(m => !skip.includes(m.kind));
    if (!marks.length) return t.text;
    let out = '';
    let i = 0;
    for (const m of [...marks].sort((a, b) => a.start - b.start)) {
      if (m.start < i) continue;
      out += t.text.slice(i, m.start);
      const body = t.text.slice(m.start, m.end);
      /* An oxidation number and a lone pair are said AFTER the atom, because that is the order
         they are read in: "iron, oxidation state plus two", not "oxidation state plus two
         iron". A highlight, a crossing out and an italic are said BEFORE what they cover,
         because they apply to the whole run that follows. */
      if (m.kind === 'oxidation') {
        out += body + hold(say('oxidationState', { state: m.value ?? '' }), true);
      } else if (m.kind === 'lewis') {
        /* Each occupied side named in reading order, and a PAIR named differently from a single
           electron — the difference is the whole of what a Lewis structure says. The two
           readings that existed before this ("lone pair above", "lone pair below") come out
           unchanged for the two cases that used to be the only ones. */
        out += body + lewisSpeech(m.value ?? '', hold);
      } else if (m.kind === 'annotation') {
        /* AFTER the species and as its own phrase, because it is a gloss on what was just
           said: "C A C O 3, calcium carbonate". It is the one mark here carrying CONTENT, so
           it is read as written rather than named — the word "annotation" is never said. */
        out += body + hold(m.value ?? '');
      } else if (m.kind === 'bar') {
        /* BEFORE, because that is where the word goes: an antiproton, not a proton anti. */
        out += hold(say('antiparticle')) + body;
      } else {
        const word = m.kind === 'color' ? 'highlighted' : m.kind === 'italic' ? 'italic' : 'crossedOut';
        out += hold(say(word)) + body;
      }
      i = m.end;
    }
    return out + t.text.slice(i);
  };

  /** A run of neighbours all wholly carrying the same mark reads as one span. */
  const run = (from: number, kind: MarkKind, value?: string): number => {
    let j = from;
    while (j + 1 < toks.length) {
      const next = covering(toks[j + 1]!, kind);
      if (!next || (kind === 'color' && next.value !== value)) break;
      j++;
    }
    return j;
  };

  const parts: string[] = [];
  let i = 0;
  while (i < toks.length) {
    const colour = covering(toks[i]!, 'color');
    const struck = !colour && covering(toks[i]!, 'strike');
    const kind: MarkKind | null = colour ? 'color' : struck ? 'strike' : null;
    if (!kind) { parts.push(inner(toks[i]!, [])); i++; continue; }

    const j = run(i, kind, colour?.value);
    parts.push(hold(say(kind === 'color' ? 'highlighted' : 'crossedOut')));
    for (let k = i; k <= j; k++) parts.push(inner(toks[k]!, [kind]));
    if (j > i) parts.push(hold(say(kind === 'color' ? 'endHighlighted' : 'endCrossedOut')));
    i = j + 1;
  }
  return parts.join(' ');
}

/**
 * Every side of a Lewis atom that has electrons on it, named in reading order.
 *
 * A PAIR is named differently from a single electron, which is the whole of what the notation
 * says: two dots are a lone pair and one is an unpaired electron, and a reader told "dots
 * above" learns neither. The two readings that existed before the sides grew — "lone pair
 * above" and "lone pair below" — come out unchanged for the two cases that used to be the only
 * ones there were.
 */
function lewisSpeech(value: string, hold: (text: string, tight?: true) => string): string {
  const dots = parseLewisValue(value);
  const parts: string[] = [];
  for (const side of LEWIS_SIDES) {
    const count = dots[side];
    if (!count) continue;
    const key = `${count === 2 ? 'lonePair' : 'unpaired'}${side[0]!.toUpperCase()}${side.slice(1)}`;
    /* NOT tight: each side is its own phrase and gets its own comma. Run together — "lone pair
       above single electron to the right lone pair below" — a reader has no pause to place one
       side before the next arrives, which is the whole of what the structure says. Reported. */
    parts.push(hold(say(key as SpeechKey)));
  }
  return parts.join('');
}

/**
 * One arrow and its labels, as a phrase.
 *
 * NO "with" in the composed forms, because the arrow names already carry their own preposition:
 * `<=>` is "in equilibrium with" and `<-` is "produced from", so "in equilibrium with, with Fe
 * above" says it twice. The position is what has to survive — a reader cannot see which side of
 * the arrow the label is on — so "above" and "below" stay.
 *
 * An unfilled decoration reads as the bare arrow: a teacher who opened a box and typed nothing
 * has written an arrow, and "yields with" trailing into nothing says less than "yields".
 */
function labelled(key: SpeechKey, aboveRaw: string, belowRaw: string): string {
  const above = isHeat(aboveRaw) ? say('heat') : isLight(aboveRaw) ? say('photon') : bareLabel(aboveRaw);
  const below = isHeat(belowRaw) ? say('heat') : isLight(belowRaw) ? say('photon') : bareLabel(belowRaw);
  if (!above && !below) return say(key);
  if (key === 'yields' && above && !below) {
    if (isHeat(aboveRaw)) return say('yieldsHeat');
    if (isLight(aboveRaw)) return say('yieldsLight');
    return say('yieldsWith', { condition: above });
  }
  if (above && below) return say('arrowWithBoth', { arrow: say(key), above, below });
  if (above) return say('arrowWithAbove', { arrow: say(key), condition: above });
  return say('arrowWithBelow', { arrow: say(key), condition: below });
}

export function reactionSpeech(formula: string): string {
  if (!formula.trim()) return '';

  /**
   * Phrases are lifted out as they are recognised and put back at the end.
   *
   * The alternative — translating in place — means the next pass runs over English words and spells
   * "yields" as "Y I E L D S". Splitting on the markers at the end keeps the two worlds apart.
   */
  const phrases: { text: string; tight?: true }[] = [];
  /**
   * `tight` for a phrase that belongs TO the species rather than separating two of them.
   *
   * A charge is part of the ion: "F E charge plus 2" is one thing, where "F E, charge plus 2"
   * pauses in the middle of it and sounds like two. Operators keep their commas, which is what
   * paces the reading.
   */
  const hold = (text: string, tight?: true) => `\uE000${phrases.push({ text, ...(tight ? { tight } : {}) }) - 1}\uE000`;
  /* Marks come off FIRST, so everything after this reads plain chemistry rather than the
     wrappers that carry them. */
  let out = markedUp(formula, hold);

  // 1. Nuclides, before anything else touches `^{...}_{...}`. Falls back to the bare symbol when
  //    the element is unknown, which keeps a typo readable instead of dropping it.
  out = out.replace(/\^\{(\d+)\}\s*_\{(\d+)\}\s*([A-Z][a-z]?)/g, (whole, mass: string, _z: string, sym: string) => {
    const name = elementName(sym);
    return name ? hold(say('nuclide', { element: name, mass })) : whole;
  });

  // 2. Particles, before charges — `e^-` would otherwise read as an electron with a charge.
  for (const [pattern, key] of PARTICLES) out = out.replace(pattern, () => hold(say(key)));

  /*
   * 3. Arrows carrying a label.
   *
   * EVERY arrow, not just `->`. The decoration control labels whichever arrow is selected, so a
   * catalysed equilibrium is now something a teacher can write — and read as `<=>` followed by
   * a literal `[Fe]`, it came out as "equilibrium bracket F E bracket".
   *
   * `->` with something above it keeps its own phrasing: "yields with heat" is what that arrow
   * has always said and is better English than the composed form. Everything else names the
   * arrow and then its labels.
   */
  for (const [code, key] of ARROWS) {
    const arrow = escapeCode(code);
    out = out.replace(new RegExp(`${arrow}\\s*\\[([^\\]]*)\\]\\s*\\[([^\\]]*)\\]`, 'g'),
      (_w, above: string, below: string) => hold(labelled(key, above, below)));
    out = out.replace(new RegExp(`${arrow}\\s*\\[([^\\]]*)\\]`, 'g'),
      (_w, above: string) => hold(labelled(key, above, '')));
  }

  /*
   * 3b. The co-substrate arc, BEFORE the plain arrows: its own `->` is inside the Agavi
   *     wrapper and would otherwise be read as a bare arrow with the wrapper's brackets and
   *     pipes spelled out around it.
   *
   *     Read as the sentence the drawing is — what joins, then what leaves — because a reader
   *     cannot see the arc and "arc" is not what it means.
   */
  out = out.replace(/\[cosub:([^\]]*)\]/g, (_w, body: string) => {
    const [, into = '', leaving = ''] = body.split('|');
    const parts = [hold(say('yields'))];
    if (into) parts.push(hold(say('coSubstrateIn', { species: into })));
    if (leaving) parts.push(hold(say('coSubstrateOut', { species: leaving })));
    return parts.join('');
  });

  // 4. Plain arrows.
  for (const [code, key] of ARROWS) {
    out = out.replace(new RegExp(escapeCode(code), 'g'), () => hold(say(key)));
  }

  // 5. State symbols, before brackets — otherwise `(s)` is read as bracket, S, bracket.
  out = out.replace(/\((s|l|g|aq)\)/g, (_w, st: string) => hold(say(STATES[st]!)));

  /*
   * 6. Conditions and the hydrate dot — BOTH SPELLINGS of each, for the same reason the arrows
   *    list both: a reaction carrying any mark reaches this pass as display text, so a struck
   *    or coloured `->[\Delta]` arrived as `Δ` and was read as the bare Greek letter.
   */
  out = out.replace(/\\cdot/g, () => hold(say('dot')));
  out = out.replace(/\^\\circ/g, () => hold(say('degrees')));
  out = out.replace(/\\Delta|\u0394/g, () => hold(say('heat')));
  out = out.replace(/h\\nu|h\u03BD/g, () => hold(say('photon')));

  /**
   * 7. Charges, and the reason they are spoken as "charge plus two".
   *
   * A charge and an operator are the same word otherwise: `Na+ + Cl-` is "Na plus plus Cl minus",
   * and no listener can tell which plus joins the reactants. Naming it is unconventional and it is
   * the only thing that resolves the collision.
   *
   * A MAGNITUDE only exists after `^`, which was worth checking rather than assuming: mhchem reads
   * a bare digit as a subscript in every case, so `NO3-` is nitrate — subscript 3, charge minus —
   * and `Fe2+` is Fe₂ with a single plus, NOT iron(II). Iron(II) has to be written `Fe^2+`. Reading
   * the digit into the charge would have renamed half the ions a teacher can type.
   */
  const charge = (sign: string, count?: string) => {
    // Spelled out rather than composed, for the same reason `say` is a switch.
    const key: SpeechKey = sign === '+'
      ? (count ? 'chargePlusN' : 'chargePlus')   // a bare `Na+` is "charge plus", not "plus 1"
      : (count ? 'chargeMinusN' : 'chargeMinus');
    return hold(say(key, count ? { count } : undefined), true);
  };
  // Superscript with a magnitude, then a bare superscript sign, then a sign attached to a species.
  out = out.replace(/\^\{?(\d+)\}?\s*([+-])/g, (_w, count: string, sign: string) => charge(sign, count));
  out = out.replace(/\^\{?([+-])\}?/g, (_w, sign: string) => charge(sign));
  out = out.replace(/(?<=[A-Za-z0-9)\]}])([+-])(?![0-9A-Za-z])/g, (_w, sign: string) => charge(sign));

  // 8. What is left of `^` and a standalone `v` are mhchem's gas and precipitate marks. After
  //    charges, deliberately: both spellings collide and only position tells them apart.
  out = out.replace(/(?<=\s|^)\^(?=\s|$)/g, () => hold(say('gasEvolved')));
  out = out.replace(/(?<=\s|^)v(?=\s|$)/g, () => hold(say('precipitates')));

  /*
   * 8b. A cell diagram's bars, and the bonds between atoms.
   *
   * The bars used to reach the end of the pipeline untouched and be spelled as the keyboard key:
   * "Z N, solid, | Z N charge plus 2". "Pipe" is not even a description — it names the key rather
   * than the notation, whose name is the phase boundary. Both the escaped forms the buttons
   * insert and the bare ones a teacher types are handled, longest first.
   *
   * A double or triple bond MUST be spoken, because it is the whole difference between two
   * molecules: `CH2=CH2` and `CH2-CH2` are not the same thing. A single bond is left as the
   * pause it already produces — a chemist reading `CH3-CH2-OH` aloud says "C H 3 C H 2 O H",
   * and naming every single bond in a chain would bury the double one that matters.
   */
  out = out.replace(/\$\\\|\$|\|\|/g, () => hold(say('saltBridge')));
  out = out.replace(/\$\|\$|\|/g, () => hold(say('phaseBoundary')));
  out = out.replace(/=/g, () => hold(say('doubleBond')));
  out = out.replace(/#/g, () => hold(say('tripleBond')));
  /* A single bond becomes a plain space rather than a word. Left alone it reached the speller
     glued to the next atom — `CH3-CH2-OH` spoke as "C H 3 -C H 2 -O H", and a stray hyphen is
     read differently by every screen reader. A space makes it sound like the same molecule
     written without explicit bonds, which is what it is. */
  out = out.replace(/(?<=[A-Za-z0-9)\]])-(?=[A-Za-z(])/g, ' ');

  // 9. The joining plus, which always carries spaces.
  out = out.replace(/\s\+\s/g, () => hold(say('plus')));

  // 10. Brackets are notation and are read as notation.
  out = out.replace(/[([]/g, () => hold(say('openBracket')));
  out = out.replace(/[)\]]/g, () => hold(say('closeBracket')));

  // Everything between the markers is species text; only that gets spelled out.
  return out
    .split(/\uE000(\d+)\uE000/)
    .map((part, i) => {
      if (i % 2 === 0) return spellSpecies(part);
      const phrase = phrases[Number(part)]!;
      return phrase.tight ? ` ${phrase.text} ` : `, ${phrase.text}, `;
    })
    .join('')
    .replace(/\s+/g, ' ')
    .replace(/\s+,/g, ',')
    .replace(/,\s*(?=,)/g, '')
    .replace(/^[\s,]+|[\s,]+$/g, '')
    .trim();
}
